// ClinicVoiceDictate — clean-room "Voice Type" control for the clinic top bar.
//
// A compact blue mic chip that opens a small floating popup. The popup's mic
// button runs the browser's Web Speech API and streams the transcript to the
// note editor by dispatching `vmai:voice-dictate-result` window CustomEvents.
// MedStudentClinicPage listens for those events and writes text into Quill;
// this component NEVER touches the editor directly (no prop-drilling through
// the 9k-line clinic page).
//
// FLICKER-PROOFING (the old popup visibly disappeared/reappeared whenever the
// busy clinic page re-rendered — there is a 1 Hz interval + lots of churn):
//   1. ALL state lives here (open, status, transcript, error, drag position).
//      Nothing is lifted into the parent, so parent re-renders can't reset it.
//   2. The component takes NO props and is wrapped in React.memo, so a parent
//      re-render never even re-renders this subtree — it only re-renders on
//      its OWN local state changes.
//   3. The popup is rendered through a portal into document.body (outside the
//      churny #clinic-root tree) and stays mounted until the user closes it.
//   4. Styling comes from a CSS file imported once — we never inject a <style>
//      tag in render (that tears down/rebuilds the stylesheet = a flicker
//      source). Only the drag position uses inline styles.
//
// NOTE: this is a deliberate REPLACEMENT for VoiceTyping.js / VoiceTypingPopup.js
// (still used by other pages). Do not couple back to them.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './ClinicVoiceDictate.css';

const EVENT_NAME = 'vmai:voice-dictate-result';
const POPUP_WIDTH = 264;
const RESTART_DELAY_MS = 250;      // small gap before auto-restarting after onend
const NETWORK_ERROR_LIMIT = 3;     // give up after this many consecutive network errors

function getSpeechRecognitionCtor() {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}

// Single choke point for the integration contract. The clinic listener reads
// `detail.isFinal !== false`, so we ALWAYS pass an explicit boolean.
function emitResult(transcriptText, isFinal, keepInterim) {
  try {
    window.dispatchEvent(new CustomEvent(EVENT_NAME, {
      detail: {
        transcriptText: transcriptText || '',
        isFinal: !!isFinal,
        keepInterim: !!keepInterim,
      },
    }));
  } catch (_) { /* non-fatal */ }
}

function MicIcon({ size = 16 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 1.5a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0v-7a3 3 0 0 0-3-3z" />
      <path d="M19 10.5v1a7 7 0 0 1-14 0v-1" />
      <line x1="12" y1="18.5" x2="12" y2="22" />
      <line x1="8" y1="22" x2="16" y2="22" />
    </svg>
  );
}

function StopIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <rect x="6" y="6" width="12" height="12" rx="2.5" />
    </svg>
  );
}

function ClinicVoiceDictate() {
  // Web Speech availability is static per browser — compute once.
  const [supported] = useState(() => !!getSpeechRecognitionCtor());

  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState('idle');   // 'idle' | 'connecting' | 'recording'
  const [transcript, setTranscript] = useState(''); // live preview shown in the popup
  const [error, setError] = useState(null);
  const [pos, setPos] = useState(null);            // { x, y } top-left of the popup card

  const chipRef = useRef(null);

  // Recognition machinery kept in refs so it survives re-renders untouched.
  const recognitionRef = useRef(null);
  const shouldRunRef = useRef(false);   // "keep running" guard — false after an intentional stop
  const restartTimerRef = useRef(null);
  const startingRef = useRef(false);    // guards against double-start races
  const networkErrorsRef = useRef(0);
  const noResultTimerRef = useRef(null); // fires if recognition never yields a result
  const gotResultRef = useRef(false);
  const dragRef = useRef(null);         // { startX, startY, originX, originY } while dragging

  // ── Recognition lifecycle ─────────────────────────────────────────────
  const teardownRecognition = useCallback(() => {
    shouldRunRef.current = false;
    if (restartTimerRef.current) {
      clearTimeout(restartTimerRef.current);
      restartTimerRef.current = null;
    }
    const rec = recognitionRef.current;
    if (rec) {
      // Detach FIRST so a late onend can't schedule a restart, and a late
      // onresult can't insert text after we've decided to stop.
      rec.onresult = null;
      rec.onerror = null;
      rec.onend = null;
      rec.onstart = null;
      try { rec.stop(); } catch (_) { /* already stopped */ }
      recognitionRef.current = null;
    }
    startingRef.current = false;
    networkErrorsRef.current = 0;
    if (noResultTimerRef.current) {
      clearTimeout(noResultTimerRef.current);
      noResultTimerRef.current = null;
    }
    gotResultRef.current = false;
  }, []);

  // Intentional stop (button/close/toggle). Emits the keepInterim signal so
  // the listener drops its pending-interim tracking WITHOUT deleting whatever
  // half-finished word is currently on screen.
  const stopDictation = useCallback(() => {
    teardownRecognition();
    emitResult('', false, true);
    setStatus('idle');
    setTranscript('');
  }, [teardownRecognition]);

  const startDictation = useCallback(() => {
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) {
      setError('Live transcription needs a Chromium browser like Chrome or Edge.');
      return;
    }
    // Don't stack instances / double-start.
    if (startingRef.current || recognitionRef.current) return;

    setError(null);
    setTranscript('');
    setStatus('connecting');
    shouldRunRef.current = true;
    startingRef.current = true;

    let rec;
    try {
      rec = new Ctor();
    } catch (_) {
      shouldRunRef.current = false;
      startingRef.current = false;
      setStatus('idle');
      setError('Could not start voice recognition. Please try again.');
      return;
    }
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-US';

    rec.onstart = () => {
      startingRef.current = false;
      networkErrorsRef.current = 0;
      // Auto-restarts (below) keep us in 'recording'; only surface 'connecting'
      // on the very first spin-up so the button doesn't flash amber per word.
      setStatus('recording');
      // Watchdog: Safari's engine can sit "listening" forever with zero
      // results — its no-speech errors are treated as benign below and it
      // just auto-restarts. Classic when the visit recorder shares the mic
      // (Leejoe 2026-07-08: "it listened but didn't write anything"). If
      // nothing arrives in 10s, say so instead of looking busy; the first
      // real result cancels this permanently for the session.
      if (!gotResultRef.current && !noResultTimerRef.current) {
        noResultTimerRef.current = setTimeout(() => {
          noResultTimerRef.current = null;
          if (!shouldRunRef.current || gotResultRef.current) return;
          setError('Listening, but no speech is coming through. If the visit recorder is running, Safari usually starves dictation — pause the recording, use Mac dictation (fn key), or dictate in Chrome.');
        }, 10000);
      }
    };

    rec.onresult = (event) => {
      if (!gotResultRef.current) {
        gotResultRef.current = true;
        if (noResultTimerRef.current) {
          clearTimeout(noResultTimerRef.current);
          noResultTimerRef.current = null;
        }
        setError(null);
      }
      let interim = '';
      let finalText = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const res = event.results[i];
        const chunk = res && res[0] ? res[0].transcript : '';
        if (res.isFinal) finalText += chunk;
        else interim += chunk;
      }
      const finalTrimmed = finalText.trim();
      if (finalTrimmed) emitResult(finalTrimmed, true, false);
      // Always emit the running interim — even when empty — so a just-finalized
      // word's on-screen preview clears in the listener.
      emitResult(interim, false, false);
      setTranscript(interim || finalTrimmed || '');
    };

    rec.onerror = (event) => {
      const err = event && event.error;
      if (err === 'not-allowed' || err === 'service-not-allowed') {
        teardownRecognition();
        setStatus('idle');
        setTranscript('');
        setError('Microphone access is blocked. Allow mic permission in your browser, then try again.');
        return;
      }
      if (err === 'network') {
        networkErrorsRef.current += 1;
        if (networkErrorsRef.current >= NETWORK_ERROR_LIMIT) {
          teardownRecognition();
          setStatus('idle');
          setTranscript('');
          setError('Trouble reaching the speech service. Check your connection and try again.');
        }
        // Otherwise let onend auto-restart.
        return;
      }
      // 'no-speech' / 'aborted' / 'audio-capture' are benign — auto-restart handles them.
    };

    rec.onend = () => {
      // Chrome fires onend after nearly every utterance even with
      // continuous=true. Auto-restart while the user still wants to record.
      startingRef.current = false;
      if (!shouldRunRef.current) return;          // intentional stop — don't restart
      if (restartTimerRef.current) return;        // a restart is already scheduled
      restartTimerRef.current = setTimeout(() => {
        restartTimerRef.current = null;
        if (!shouldRunRef.current) return;
        const cur = recognitionRef.current;
        if (!cur) return;
        try { cur.start(); } catch (_) { /* already started — ignore */ }
      }, RESTART_DELAY_MS);
    };

    recognitionRef.current = rec;
    try {
      rec.start();
    } catch (_) {
      // Rare synchronous throw — reset to a clean idle state.
      teardownRecognition();
      setStatus('idle');
      setError('Could not start voice recognition. Please try again.');
    }
  }, [teardownRecognition]);

  const toggleMic = useCallback(() => {
    if (status === 'idle') startDictation();
    else stopDictation();
  }, [status, startDictation, stopDictation]);

  // ── Open / close ──────────────────────────────────────────────────────
  const openPopup = useCallback(() => {
    // Anchor the popup just under the chip, clamped inside the viewport.
    const rect = chipRef.current && chipRef.current.getBoundingClientRect();
    if (rect) {
      const x = clamp(rect.right - POPUP_WIDTH, 8, window.innerWidth - POPUP_WIDTH - 8);
      const y = clamp(rect.bottom + 8, 8, window.innerHeight - 120);
      setPos({ x, y });
    } else {
      setPos({ x: Math.max(8, window.innerWidth - POPUP_WIDTH - 16), y: 72 });
    }
    setError(null);
    setOpen(true);
  }, []);

  const closePopup = useCallback(() => {
    stopDictation();
    setError(null);
    setOpen(false);
  }, [stopDictation]);

  const onChipClick = useCallback(() => {
    if (open) closePopup();
    else openPopup();
  }, [open, openPopup, closePopup]);

  // ── Drag (grab the header to reposition) ──────────────────────────────
  const onHeaderMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    const rect = e.currentTarget.parentElement.getBoundingClientRect();
    dragRef.current = { startX: e.clientX, startY: e.clientY, originX: rect.left, originY: rect.top };

    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      const nx = clamp(d.originX + (ev.clientX - d.startX), 0, window.innerWidth - 60);
      const ny = clamp(d.originY + (ev.clientY - d.startY), 0, window.innerHeight - 40);
      setPos({ x: nx, y: ny });
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    e.preventDefault();
  }, []);

  // ── Full cleanup on unmount (page navigation) ─────────────────────────
  useEffect(() => () => {
    teardownRecognition();
    emitResult('', false, true); // let the listener drop tracking but keep text
  }, [teardownRecognition]);

  const isRecording = status === 'recording';
  const isConnecting = status === 'connecting';

  let statusText = 'Tap the mic to dictate';
  if (!supported) statusText = '';
  else if (isRecording) statusText = 'Listening… tap to stop';
  else if (isConnecting) statusText = 'Connecting…';

  const popup = open ? createPortal(
    <div
      className="cvd-popup"
      style={{ left: pos ? pos.x : 0, top: pos ? pos.y : 0 }}
      role="dialog"
      aria-label="Voice Type dictation"
      // Swallow mousedown so a click inside the card never bubbles up into the
      // busy clinic page (belt-and-suspenders; the portal is on document.body).
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="cvd-popup-header" onMouseDown={onHeaderMouseDown}>
        <span className="cvd-popup-title">
          <MicIcon size={14} />
          Voice Type
        </span>
        <button
          type="button"
          className="cvd-close"
          aria-label="Close voice dictation"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={closePopup}
        >
          {'×'}
        </button>
      </div>

      <div className="cvd-popup-body">
        <button
          type="button"
          className={`cvd-mic${isRecording ? ' is-recording' : ''}${isConnecting ? ' is-connecting' : ''}`}
          onClick={toggleMic}
          disabled={!supported}
          aria-label={isRecording ? 'Stop dictation' : 'Start dictation'}
          aria-pressed={isRecording}
        >
          {isConnecting
            ? <span className="cvd-spinner" />
            : isRecording
              ? <StopIcon size={22} />
              : <MicIcon size={26} />}
        </button>

        {statusText ? (
          <div className={`cvd-status${isRecording ? ' is-recording' : ''}`}>{statusText}</div>
        ) : null}

        {!supported ? (
          <div className="cvd-note">
            Live transcription needs a Chromium browser like Chrome or Edge.
          </div>
        ) : error ? (
          <div className="cvd-error">{error}</div>
        ) : transcript ? (
          <div className="cvd-transcript">{transcript}</div>
        ) : null}
      </div>
    </div>,
    document.body,
  ) : null;

  return (
    <>
      <button
        ref={chipRef}
        type="button"
        className={`cvd-chip${open ? ' is-open' : ''}`}
        title="Voice Type — speak; transcript inserts at the editor cursor"
        aria-label="Voice Type"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={onChipClick}
      >
        <MicIcon size={16} />
        <span className="cvd-chip-label">Voice Type</span>
      </button>
      {popup}
    </>
  );
}

// No props → React.memo means a parent re-render never re-renders this subtree.
export default React.memo(ClinicVoiceDictate);
